import { Modal } from 'antd';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import getBankAccounts, { PRIMARY, Response } from '../api/getBankAccounts';
import { captureError, captureErrorString } from '../pages/captureError';
import { GO_TO_SETTINGS } from '../routes/paths';
import { track } from '../tracking';
import { openCrispChat } from '../pages/utils';

export const BankAccounts: React.FC<{
    isVisible?: boolean;
    closeMe?: () => void;
    isForModal?: boolean;
    displayText?: string;
}> = ({ isVisible, closeMe, isForModal, displayText }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [bankAccounts, setBankAccounts] = useState<Response[]>([]);

    useEffect(() => {
        getAllBankAccounts();
    }, []);

    const getAllBankAccounts = async () => {
        try {
            const { success, response, error = '' } = await getBankAccounts();
            if (success && response) {
                if (Array.isArray(response)) {
                    setBankAccounts(response);
                }
            } else {
                captureErrorString(error);
                track('ERROR', { on: 'getBankAccounts', what: error });
            }
        } catch (error) {
            captureError(error as Error);
            track('ERROR', {
                on: 'getBankAccounts',
                error: (error as Error).message,
            });
        } finally {
            setIsLoading(false);
        }
    };

    return isForModal ? (
        <Modal
            onCancel={closeMe}
            width={350}
            closable={false}
            visible={isVisible}
            destroyOnClose
            footer={null}
            bodyStyle={{
                padding: '0px',
                maxHeight: '75vh',
                display: 'flex',
                flexDirection: 'column',
            }}
            className="overflow-hidden rounded-8"
            style={{ maxHeight: '75vh' }}
        >
            <BankAccountsComponent
                isLoading={isLoading}
                closeMe={closeMe}
                bankAccounts={bankAccounts}
                isForModal={Boolean(isForModal)}
                displayText={displayText}
            />
        </Modal>
    ) : (
        <BankAccountsComponent
            isLoading={isLoading}
            closeMe={closeMe}
            bankAccounts={bankAccounts}
            isForModal={Boolean(isForModal)}
            displayText={displayText}
        />
    );
};

export const BankAccountsComponent: React.FC<{
    isLoading: boolean;
    closeMe?: () => void;
    bankAccounts: Response[];
    isForModal?: boolean;
    displayText?: string;
}> = ({ isLoading, closeMe, bankAccounts, isForModal, displayText }) => {
    const history = useHistory();
    const onLinkBankAccount = () => {
        history.push(GO_TO_SETTINGS('activate-payments'));
        track('LINK_BANK_ACCOUNT');
    };
    const onNeedHelp = () => {
        openCrispChat(
            'NEED_HELP_HXNG',
            {
                from: 'Order Dashboard',
                type: 'bank accounts',
            },
            `I need help with My Bank Accounts`,
        );
    };
    return (
        <div className="flex flex-col overflow-hidden">
            {isForModal && (
                <div className="flex items-center justify-between px-16 py-14">
                    <p className="text-20">My Bank Account(s)</p>
                    <p className="cursor-pointer" onClick={closeMe}>
                        <svg
                            className="w-14 h-14"
                            viewBox="0 0 12 12"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M10.7812 2.28125L7.03125 6.03125L10.75 9.75C11.0625 10.0312 11.0625 10.5 10.75 10.7812C10.4688 11.0938 10 11.0938 9.71875 10.7812L5.96875 7.0625L2.25 10.7812C1.96875 11.0938 1.5 11.0938 1.21875 10.7812C0.90625 10.5 0.90625 10.0312 1.21875 9.71875L4.9375 6L1.21875 2.28125C0.90625 2 0.90625 1.53125 1.21875 1.21875C1.5 0.9375 1.96875 0.9375 2.28125 1.21875L6 4.96875L9.71875 1.25C10 0.9375 10.4688 0.9375 10.7812 1.25C11.0625 1.53125 11.0625 2 10.7812 2.28125Z"
                                fill="#1A1A1A"
                            />
                        </svg>
                    </p>
                </div>
            )}

            <div
                className={`flex-1 overflow-scroll ${
                    isForModal ? 'border-t border-b bg-darwin-neutrals-50 px-16 pb-16' : ''
                } border-darwin-neutral_new-200 pt-16`}
            >
                {isLoading ? (
                    <div
                        className="flex items-center justify-center"
                        style={{ height: 'calc(75vh - 56px)' }}
                    >
                        <svg
                            className="w-24 h-24 fill-darwin-accent-bright-blue-800 animate-spin"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 512 512"
                        >
                            <path d="M96 256c0-26.5-21.5-48-48-48S0 229.5 0 256s21.5 48 48 48S96 282.5 96 256zM108.9 60.89c-26.5 0-48.01 21.49-48.01 47.99S82.39 156.9 108.9 156.9s47.99-21.51 47.99-48.01S135.4 60.89 108.9 60.89zM108.9 355.1c-26.5 0-48.01 21.51-48.01 48.01S82.39 451.1 108.9 451.1s47.99-21.49 47.99-47.99S135.4 355.1 108.9 355.1zM256 416c-26.5 0-48 21.5-48 48S229.5 512 256 512s48-21.5 48-48S282.5 416 256 416zM464 208C437.5 208 416 229.5 416 256s21.5 48 48 48S512 282.5 512 256S490.5 208 464 208zM403.1 355.1c-26.5 0-47.99 21.51-47.99 48.01S376.6 451.1 403.1 451.1s48.01-21.49 48.01-47.99S429.6 355.1 403.1 355.1zM256 0C229.5 0 208 21.5 208 48S229.5 96 256 96s48-21.5 48-48S282.5 0 256 0z"></path>
                        </svg>
                    </div>
                ) : bankAccounts.length > 0 ? (
                    <div
                        className="space-y-16"
                        style={isForModal ? { height: 'calc(75vh - 120px)' } : {}}
                    >
                        <p className="text-16 font-700" style={{ color: '#1A1A1A' }}>
                            {bankAccounts.length} {displayText} bank account(s) found
                        </p>

                        <div className={`${isForModal ? 'space-y-16' : 'flex flex-wrap gap-16'}`}>
                            {bankAccounts.map((i) => {
                                const {
                                    account_type,
                                    bank_account,
                                    vpa,
                                    active,
                                    account_category,
                                } = i;
                                const isVpaType = account_type?.toLowerCase() === 'vpa';
                                const style = !isForModal ? { minWidth: 320 } : undefined;
                                return (
                                    <div
                                        className={`p-16 bg-white rounded-8 space-y-12 ${
                                            !isForModal ? 'border' : ''
                                        } border-darwin-neutral_new-200`}
                                        style={style}
                                    >
                                        {isVpaType ? (
                                            <div className="flex items-center space-x-4">
                                                <p>
                                                    <svg
                                                        className="w-14 h-16"
                                                        viewBox="0 0 14 16"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                        <path
                                                            fill-rule="evenodd"
                                                            clip-rule="evenodd"
                                                            d="M8.86761 0.416656L13.5 7.99999L3.75 15.5833L4.90999 12.1471L10.2399 7.99999L7.70762 3.85285L8.86761 0.416656Z"
                                                            fill="#0E8635"
                                                        />
                                                        <path
                                                            fill-rule="evenodd"
                                                            clip-rule="evenodd"
                                                            d="M5.61762 0.416656L10.25 7.99999L0.5 15.5833L5.61762 0.416656Z"
                                                            fill="#E97208"
                                                        />
                                                    </svg>
                                                </p>
                                                <p
                                                    className="text-16 font-700"
                                                    style={{ color: '#767676' }}
                                                >
                                                    UPI
                                                </p>
                                            </div>
                                        ) : bank_account ? (
                                            <div className="flex items-center space-x-4">
                                                <p className="relative -top-1">
                                                    <svg
                                                        className="w-16 h-16"
                                                        viewBox="0 0 16 16"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                        <path
                                                            d="M7.66992 1.70117C7.87305 1.625 8.10156 1.625 8.30469 1.70117L13.9922 4.13867C14.3477 4.29102 14.5508 4.67188 14.4746 5.05273C14.3984 5.43359 14.0684 5.6875 13.6875 5.6875V5.89062C13.6875 6.24609 13.4082 6.5 13.0781 6.5H2.92188C2.56641 6.5 2.3125 6.24609 2.3125 5.89062V5.6875C1.90625 5.6875 1.57617 5.43359 1.5 5.05273C1.42383 4.67188 1.62695 4.29102 1.98242 4.13867L7.66992 1.70117ZM4.75 7.3125V12.1875H5.76562V7.3125H7.39062V12.1875H8.60938V7.3125H10.2344V12.1875H11.25V7.3125H12.8496V12.3145C12.875 12.3145 12.9004 12.3145 12.9004 12.3398L14.1191 13.1523C14.4238 13.3555 14.5508 13.7109 14.4492 14.0664C14.3477 14.3965 14.043 14.625 13.6875 14.625H2.3125C1.93164 14.625 1.62695 14.3965 1.52539 14.0664C1.42383 13.7109 1.55078 13.3555 1.85547 13.1523L3.07422 12.3398C3.07422 12.3145 3.09961 12.3145 3.125 12.3145V7.3125H4.75ZM8 3.25C7.54297 3.25 7.1875 3.63086 7.1875 4.0625C7.1875 4.51953 7.54297 4.875 8 4.875C8.43164 4.875 8.8125 4.51953 8.8125 4.0625C8.8125 3.63086 8.43164 3.25 8 3.25Z"
                                                            fill="#0E8635"
                                                        />
                                                    </svg>
                                                </p>
                                                <p
                                                    className="text-16 font-700 truncate-overflow-1"
                                                    style={{ color: '#767676' }}
                                                >
                                                    {bank_account?.bank_name}
                                                </p>
                                            </div>
                                        ) : null}

                                        <div className="text-13" style={{ color: '#1A1A1A' }}>
                                            {isVpaType ? (
                                                <div className="space-y-6">
                                                    <div className="flex items-center justify-between space-x-8">
                                                        <p className="font-700">UPI Address</p>
                                                        <p>{vpa?.address}</p>
                                                    </div>
                                                    <div className="flex items-center justify-between space-x-8">
                                                        <p className="font-700">Bank Name</p>
                                                        <p className="uppercase">{vpa?.handle}</p>
                                                    </div>
                                                </div>
                                            ) : bank_account ? (
                                                <div className="space-y-6">
                                                    <div className="flex items-center justify-between space-x-8">
                                                        <p className="font-700">Account Number</p>
                                                        <p>{bank_account?.account_number}</p>
                                                    </div>
                                                    <div className="flex items-center justify-between space-x-8">
                                                        <p className="font-700">IFSC Code</p>
                                                        <p>{bank_account?.ifsc}</p>
                                                    </div>
                                                    <div className="flex items-center justify-between space-x-8">
                                                        <p className="font-700">Holder Name</p>
                                                        <p>{bank_account?.name}</p>
                                                    </div>
                                                </div>
                                            ) : null}
                                        </div>

                                        <div className="flex items-center space-x-14 text-13">
                                            {active ? (
                                                <div className="px-8 py-3 rounded-4 flex items-center space-x-4 bg-darwin-green-primary">
                                                    <p>
                                                        <svg
                                                            className="w-14 h-14"
                                                            viewBox="0 0 14 14"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                        >
                                                            <path
                                                                d="M7 13.625C4.66406 13.625 2.53125 12.4062 1.36328 10.375C0.195312 8.36914 0.195312 5.90625 1.36328 3.875C2.53125 1.86914 4.66406 0.625 7 0.625C9.31055 0.625 11.4434 1.86914 12.6113 3.875C13.7793 5.90625 13.7793 8.36914 12.6113 10.375C11.4434 12.4062 9.31055 13.625 7 13.625ZM9.86914 5.93164H9.84375C10.0977 5.70312 10.0977 5.32227 9.84375 5.06836C9.61523 4.83984 9.23438 4.83984 9.00586 5.06836L6.1875 7.91211L4.99414 6.71875C4.74023 6.46484 4.35938 6.46484 4.13086 6.71875C3.87695 6.94727 3.87695 7.32812 4.13086 7.55664L5.75586 9.18164C5.98438 9.43555 6.36523 9.43555 6.61914 9.18164L9.86914 5.93164Z"
                                                                fill="#E8F6F0"
                                                            />
                                                        </svg>
                                                    </p>
                                                    <p style={{ color: '#E8F6F0' }}>Active</p>
                                                </div>
                                            ) : (
                                                <div
                                                    className="px-8 py-3 rounded-4 flex items-center space-x-4"
                                                    style={{ backgroundColor: '#484848' }}
                                                >
                                                    <p>
                                                        <svg
                                                            className="w-16 h-16"
                                                            viewBox="0 0 16 16"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                        >
                                                            <path
                                                                d="M8 14.625C5.66406 14.625 3.53125 13.4062 2.36328 11.375C1.19531 9.36914 1.19531 6.90625 2.36328 4.875C3.53125 2.86914 5.66406 1.625 8 1.625C10.3105 1.625 12.4434 2.86914 13.6113 4.875C14.7793 6.90625 14.7793 9.36914 13.6113 11.375C12.4434 13.4062 10.3105 14.625 8 14.625ZM8 4.875C7.64453 4.875 7.39062 5.1543 7.39062 5.48438V8.32812C7.39062 8.68359 7.64453 8.9375 8 8.9375C8.33008 8.9375 8.60938 8.68359 8.60938 8.32812V5.48438C8.60938 5.1543 8.33008 4.875 8 4.875ZM7.1875 10.5625C7.1875 11.0195 7.54297 11.375 8 11.375C8.43164 11.375 8.8125 11.0195 8.8125 10.5625C8.8125 10.1309 8.43164 9.75 8 9.75C7.54297 9.75 7.1875 10.1309 7.1875 10.5625Z"
                                                                fill="white"
                                                            />
                                                        </svg>
                                                    </p>
                                                    <p className="text-white">Inactive</p>
                                                </div>
                                            )}
                                            {account_category === PRIMARY && (
                                                <div className="px-8 py-3 rounded-4 flex items-center space-x-4 bg-darwin-accent-bright-blue-800">
                                                    <p>
                                                        <svg
                                                            className="w-14 h-14"
                                                            viewBox="0 0 16 16"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                        >
                                                            <path
                                                                d="M8.71094 2.08203L10.3613 5.45898L13.9922 5.99219C14.2969 6.04297 14.5508 6.24609 14.6523 6.55078C14.7539 6.83008 14.6777 7.16016 14.4492 7.36328L11.8086 9.97852L12.4434 13.6855C12.4941 13.9902 12.3672 14.2949 12.1133 14.4727C11.8594 14.6758 11.5293 14.6758 11.25 14.5488L8 12.7969L4.72461 14.5488C4.4707 14.6758 4.14062 14.6758 3.88672 14.4727C3.63281 14.2949 3.50586 13.9902 3.55664 13.6855L4.16602 9.97852L1.52539 7.36328C1.32227 7.16016 1.24609 6.83008 1.32227 6.55078C1.42383 6.24609 1.67773 6.04297 1.98242 5.99219L5.63867 5.45898L7.26367 2.08203C7.39062 1.80273 7.66992 1.625 8 1.625C8.30469 1.625 8.58398 1.80273 8.71094 2.08203Z"
                                                                fill="#E9EFFF"
                                                            />
                                                        </svg>
                                                    </p>
                                                    <p style={{ color: '#E9EFFF' }}>Primary</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {isForModal && <div className="h-1"></div>}
                    </div>
                ) : isForModal ? (
                    <div
                        className="flex items-center justify-center text-16"
                        style={{ height: 'calc(75vh - 56px)' }}
                    >
                        <button
                            className="px-12 py-10 bg-darwin-accent-bright-blue-800 text-white text-14 font-700 rounded-8"
                            onClick={onLinkBankAccount}
                        >
                            Link Bank Account
                        </button>
                    </div>
                ) : null}
            </div>

            {isForModal && bankAccounts.length > 0 && (
                <div className="p-12 flex items-center justify-between">
                    <button
                        className="border border-darwin-accent-bright-blue-800 flex items-center space-x-10 rounded-8 px-12 py-8 font-700"
                        onClick={onNeedHelp}
                    >
                        <p>
                            <svg
                                className="min-w-16 h-18 fill-darwin-accent-bright-blue-800"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 512 512"
                            >
                                <path d="M0 64C0 28.7 28.7 0 64 0H448c35.3 0 64 28.7 64 64V352c0 35.3-28.7 64-64 64H309.3L185.6 508.8c-4.8 3.6-11.3 4.2-16.8 1.5s-8.8-8.2-8.8-14.3V416H64c-35.3 0-64-28.7-64-64V64zm169.8 53.3l-.4 1.2c-4.4 12.5 2.1 26.2 14.6 30.6s26.2-2.1 30.6-14.6l.4-1.2c1.1-3.2 4.2-5.3 7.5-5.3h58.3c8.4 0 15.1 6.8 15.1 15.1c0 5.4-2.9 10.4-7.6 13.1l-44.3 25.4c-7.5 4.3-12.1 12.2-12.1 20.8V216c0 13.3 10.7 24 24 24c13.1 0 23.8-10.5 24-23.6l32.3-18.5c19.6-11.3 31.7-32.2 31.7-54.8c0-34.9-28.3-63.1-63.1-63.1H222.6c-23.7 0-44.8 14.9-52.8 37.3zM288 304a32 32 0 1 0 -64 0 32 32 0 1 0 64 0z" />
                            </svg>
                        </p>
                        <p className="text-darwin-accent-bright-blue-800">Issue with Accounts?</p>
                    </button>
                    <button
                        className="px-12 py-10 bg-darwin-accent-bright-blue-800 text-white text-14 font-700 rounded-8"
                        onClick={closeMe}
                    >
                        Close
                    </button>
                </div>
            )}
        </div>
    );
};
